import React, { useState, useEffect } from 'react';
import { calculateProfits, formatCurrency } from '../../utils/calculations';
import {useTranslation} from "react-i18next";

// Данные о продуктах
const tradingProducts = [
  {
    "id": "1",
    "type": "SILVER",
    "category": "TRADING",
    "productType": "TRADING",
    "minAmount": 50,
    "minPeriod": 4,
    "maxPeriod": 12,
    "penaltyPercent": 10,
    "dailyRate": {"min": 0.63, "max": 0.7},
    "weeklyRate": {"min": 4.41, "max": 4.9},
    "monthlyRate": {"min": 18.9, "max": 21},
    "yearlyRate": {"min": 226.8, "max": 252}
  },
  {
    "id": "2",
    "type": "GOLD",
    "category": "TRADING",
    "productType": "TRADING",
    "minAmount": 5000,
    "minPeriod": 3,
    "maxPeriod": 12,
    "penaltyPercent": 10,
    "dailyRate": {"min": 0.7, "max": 0.81},
    "weeklyRate": {"min": 4.9, "max": 5.67},
    "monthlyRate": {"min": 21, "max": 24.3},
    "yearlyRate": {"min": 252, "max": 291.6}
  },
  {
    "id": "3",
    "type": "PLATINUM",
    "category": "TRADING",
    "productType": "TRADING",
    "minAmount": 10000,
    "minPeriod": 2,
    "maxPeriod": 12,
    "penaltyPercent": 10,
    "dailyRate": {"min": 0.81, "max": 0.95},
    "weeklyRate": {"min": 5.67, "max": 6.65},
    "monthlyRate": {"min": 24.3, "max": 28.5},
    "yearlyRate": {"min": 291.6, "max": 342}
  }
];

// Для других категорий (примеры)
const stakingProduct = {
    "id": "4",
    "type": "STAKING",
    "category": "STAKING",
    "productType": "STAKING",
    "minAmount": 500,
    "minPeriod": 4,
    "maxPeriod": 12,
    "penaltyPercent": 10,
    "dailyRate": {"min": 0.45, "max": 0.83},
    "weeklyRate": {"min": 3.15, "max": 5.81},
    "monthlyRate": {"min": 13.5, "max": 24.9},
    "yearlyRate": {"min": 162, "max": 298.8}
};

const thorchainProduct = {
    "id": "5",
    "type": "THORCHAIN",
    "category": "THORCHAIN",
    "productType": "THORCHAIN",
    "minAmount": 500,
    "minPeriod": 6,
    "maxPeriod": 12,
    "penaltyPercent": 10,
    "dailyRate": {"min": 0.3, "max": 2.1},
    "weeklyRate": {"min": 2.1, "max": 14.7},
    "monthlyRate": {"min": 9, "max": 63},
    "yearlyRate": {"min": 468, "max": 468}
  };

interface ProfitCalculatorProps {
  productId: string; // Может быть 'trading', 'staking', и т.д.
  initialAmount?: number;
  showWeeklyPayouts?: boolean;
}

const ProfitCalculator: React.FC<ProfitCalculatorProps> = ({
  productId,
  initialAmount = 50,
  showWeeklyPayouts = true
}) => {
  // Добавляем состояние для выбранного подпродукта (для TRADING)
  const [selectedProductType, setSelectedProductType] = useState<string>("");
  const [amount, setAmount] = useState<number>(initialAmount);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  
  // Флаг для отслеживания ручного выбора пользователя
  const [userSelectedProduct, setUserSelectedProduct] = useState<boolean>(false);
  
  // Результаты расчетов
  const [dailyRate, setDailyRate] = useState<number>(0);
  const [weeklyRate, setWeeklyRate] = useState<number>(0);
  const [monthlyRate, setMonthlyRate] = useState<number>(0);
  const [yearlyRate, setYearlyRate] = useState<number>(0);
  
  const [dailyProfit, setDailyProfit] = useState<number>(0);
  const [weeklyProfit, setWeeklyProfit] = useState<number>(0);
  const [monthlyProfit, setMonthlyProfit] = useState<number>(0);
  const [yearlyProfit, setYearlyProfit] = useState<number>(0);

  // Функция для поиска подходящего продукта по сумме
  const findSuitableProduct = (amount: number) => {
    // Сортируем продукты по минимальной сумме (от большей к меньшей)
    const sortedProducts = [...tradingProducts].sort((a, b) => b.minAmount - a.minAmount);
    
    // Находим первый подходящий продукт
    return sortedProducts.find(product => amount >= product.minAmount) || tradingProducts[0];
  };

  // Эффект инициализации - выполняется только при первом рендере
  useEffect(() => {
    // Устанавливаем начальный подпродукт по минимальной сумме
    if (productId === 'trading') {
      // Находим подходящий продукт по сумме
      const product = findSuitableProduct(initialAmount);
      if (product) {
        setSelectedProductType(product.type);
        setSelectedProduct(product);
      }
    } else if (productId === 'staking') {
      setSelectedProduct(stakingProduct);
    } else if (productId === 'thorchain') {
      setSelectedProduct(thorchainProduct);
    } 
  }, [productId, initialAmount]);

  // Эффект при изменении суммы - только автовыбор если пользователь не выбрал продукт
  useEffect(() => {
    if (productId === 'trading' && !userSelectedProduct) {
      // Автовыбор продукта только если пользователь не выбрал сам
      const product = findSuitableProduct(amount);
      if (product && selectedProductType !== product.type) {
        setSelectedProductType(product.type);
        setSelectedProduct(product);
      }
    }
  }, [amount, productId, userSelectedProduct]);

  // Эффект для расчета доходности при изменении продукта или суммы
  useEffect(() => {
    calculateRates();
  }, [amount, selectedProduct]);

  // Расчет ставок и прибыли
  const calculateRates = () => {
    if (!selectedProduct) return;
    
    let daily = 0;
    let monthly = 0;
    let yearly = 0;
    
    if (selectedProduct.dailyRate) {
      // Берем среднее значение между min и max
      daily = (selectedProduct.dailyRate.min + selectedProduct.dailyRate.max) / 2;
      monthly = (selectedProduct.monthlyRate.min + selectedProduct.monthlyRate.max) / 2;
      yearly = (selectedProduct.yearlyRate.min + selectedProduct.yearlyRate.max) / 2;
    } else if (selectedProduct.progressiveProfit) {
      // Для THORCHAIN с прогрессивной прибылью (берем средние значения)
      daily = selectedProduct.progressiveProfit["6"].dailyRate;
      monthly = selectedProduct.progressiveProfit["6"].monthlyRate;
      yearly = monthly * 12; // Приблизительный годовой процент
    }
    
    setDailyRate(daily);
    setWeeklyRate(daily * 7);
    setMonthlyRate(monthly);
    setYearlyRate(yearly);
    
    // Расчет прибыли
    setDailyProfit(amount * (daily / 100));
    setWeeklyProfit(amount * (daily * 7 / 100));
    setMonthlyProfit(amount * (monthly / 100));
    setYearlyProfit(amount * (yearly / 100));
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value.replace(/[^0-9]/g, ''), 10) || 0;
    setAmount(value);
  };

  const handleProductTypeChange = (type: string) => {
    const product = tradingProducts.find(p => p.type === type);
    if (product) {
      // Если сумма меньше минимальной для этого продукта, увеличиваем её
      
      if (amount != product.minAmount) {
        setAmount(product.minAmount);
      }
      
      // Устанавливаем флаг ручного выбора продукта
      setUserSelectedProduct(true);
      
      setSelectedProductType(type);
      setSelectedProduct(product);
    }
  };

  // Форматирование поля ввода с разделителями тысяч
  const formatInput = (value: number): string => {
    return new Intl.NumberFormat('ru-RU').format(value);
  };
  const { t } = useTranslation() as {
    t: (key: string, options?: any) => string;
  };

  return (
    <div>
      <h3 className="text-2xl md:text-3xl font-bold font-druk mb-6 md:mb-2">{t('calculatorTitle')}</h3>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-1">
        {/* Левая часть */}
        <div className="bg-[#29292B] md:bg-gray-900 rounded-3xl p-6 md:col-span-4">
          <div className="flex flex-col">
            {/* Выбор типа продукта TRADING */}
            {productId === 'trading' && (
              <div className="flex mb-6">
                <div className="flex w-full justify-center md:flex-col gap-0.5 lg:flex-row">
                  {tradingProducts.map((product, index) => (
                    <button
                      key={product.id}
                      onClick={() => handleProductTypeChange(product.type)}
                      className={`font-druk py-1 flex-1 px-2 rounded-lg font-bold md:px-2 text-base md:text-base 2xl:text-xl sm:text-sm lg:text-sm lg:px-1 xl:text-base xl:px-2 ${
                        selectedProductType === product.type
                          ? 'bg-[#D4FB00] text-black'
                          : 'bg-[#494646] md:bg-white md:bg-opacity-30 text-white'
                      }`}
                    >
                      {product.type}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Показываем название продукта если это не TRADING */}
            {productId !== 'trading' && (
              <div className="bg-[#D4FB00] py-2 px-4 mb-6 inline-self-start text-black font-bold rounded-lg">
                {productId.toUpperCase()}
              </div>
            )}
            
            {/* Поле суммы инвестиций */}
            <div className="md:mt-10">
              <p className="text-lg font-medium md:text-sm lg:text-sm xl:text-base 2xl:text-xl mb-2 text-white text-center">{t('investmentAmount')}</p>
              <input
                type="text"
                value={formatInput(amount)}
                onChange={handleAmountChange}
                className={selectedProduct && amount < selectedProduct.minAmount ? "w-full bg-black p-4 rounded text-red-600 text-center text-3xl font-bold focus:outline-none focus:ring-1 focus:ring-[#D4FB00]" :"w-full bg-black p-4 rounded text-[#D4FB00] md:text-white text-center text-3xl font-bold focus:outline-none focus:ring-1 focus:ring-[#D4FB00]"}
                aria-label="Сумма инвестиции"
                maxLength={11}
              />
              
              {/* Показываем минимальную сумму для выбранного продукта */}
              {selectedProduct && (
                <p className="text-base font-medium mt-2 text-gray-400 text-center">
                  {t('minAmount')} {formatInput(selectedProduct.minAmount)}
                </p>
              )}
            </div>
            
            {/* Полоса прогресса */}
            {/* <div className="mt-4 mb-8">
              <div className="relative pt-1">
                <div className="h-2 flex rounded">
                  <div style={{ width: "70%" }} className="bg-[#D4FB00]"></div>
                  <div style={{ width: "30%" }} className="bg-gray-700"></div>
                </div>
              </div>
            </div> */}
          </div>
        </div>
        
        {/* Правая часть - Доходность */}
        <div className="bg-[#29292B] md:bg-gray-800 rounded-3xl p-6 md:col-span-3 md:font-druk
             md:px-3 text-xl md:text-sm 2xl:text-xl sm:text-sm lg:text-sm lg:px-3 xl:text-base
            ">
          <div className="flex flex-col h-full justify-between">
            {/* День */}
            <div className="mb-4">
              <p className="text-white mb-4 md:mb-1">{t('dailyProfit')}</p>
              <div className="flex items-center justify-between">
                <span className="text-[#D4FB00] font-bold">$ {dailyProfit.toFixed(2)}</span>
                <span className="text-center font-bold ml-2 text-black bg-white md:text-white md:bg-black rounded w-28 md:px-2 py-1
                text-base md:text-base xl:text-base 2xl:text-base 
                ">{dailyRate.toFixed(3)}%</span>
              </div>
            </div>
            
            {/* Месяц */}
            <div className="mb-4 border-t-[1px] md:border-none">
              <p className="text-white mb-4 mt-4 md:mb-1 md:mt-0">{t('monthlyProfit')}</p>
              <div className="flex items-center justify-between">
                <span className="text-[#D4FB00] font-bold">$ {monthlyProfit.toFixed(1)}</span>
                <span className="text-center font-bold ml-2 text-black bg-white md:text-white md:bg-black rounded w-28 md:px-2 py-1
                text-base md:text-base xl:text-base 2xl:text-base 
                ">{monthlyRate.toFixed(2)}%</span>
              </div>
            </div>
            
            {/* Квартал */}
            <div className="mb-4 border-t-[1px] md:border-none">
              <p className="text-white mb-4 mt-4 md:mb-1 md:mt-0">{t('quarterlyProfit')}</p>
              <div className="flex items-center justify-between">
                <span className="text-[#D4FB00] font-bold">$ {(monthlyProfit * 3).toFixed(2)}</span>
                <span className="text-center font-bold ml-2 text-black bg-white md:text-white md:bg-black rounded w-28 md:px-2 py-1
                text-base md:text-base xl:text-base 2xl:text-base 
                ">{(monthlyRate * 3).toFixed(2)}%</span>
              </div>
            </div>
            
            {/* Год */}
            <div className="mb-4 border-t-[1px] md:border-none">
              <p className="text-white mb-4 mt-4 md:mb-1 md:mt-0">{t('yearlyProfit')}</p>
              <div className="flex items-center justify-between">
                <span className="text-[#D4FB00] font-bold">$ {yearlyProfit.toFixed(1)}</span>
                <span className="text-center font-bold ml-2 text-black bg-white md:text-white md:bg-black rounded w-28 md:px-2 py-1
                text-base md:text-base xl:text-base 2xl:text-base 
                ">{yearlyRate.toFixed(2)}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Блок с суммой выплаты за неделю */}
      {showWeeklyPayouts && (
        <div className="bg-[#29292B] md:bg-gray-900 rounded-3xl p-6 mt-1 md:font-druk text-center md:text-left">
          <p className="text-xl font-medium text-white mb-2">{t('weeklyPayoutsLabel')}</p>
          <div className="text-2xl
             font-bold text-[#D4FB00]">
            $ {weeklyProfit.toFixed(2)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfitCalculator;
